import {
  CHART_LIMITS,
  HEX_COLOUR_RE,
  REQUIRED_PALETTE_KEYS,
  findNearestOnset,
  type BaseChart,
  type ChartValidationIssue,
  type ChartValidationResult,
} from './chart.js';
import { CIRCLES_VARIANTS } from './gameTypes.js';
import type { SliderCurveType, Vec2 } from './osuCurves.js';

/**
 * Width / height of the osu! standard playfield in osu units. We persist
 * hit-circle positions in this coordinate space so the same chart can be
 * rendered at any canvas resolution by mapping to local pixel space.
 */
export const OSU_PLAYFIELD_WIDTH = 512;
export const OSU_PLAYFIELD_HEIGHT = 384;

export type CircleHitObjectKind = 'circle' | 'spinner' | 'slider';

/**
 * osu! sample set governing the *timbre* of imported hitsounds — the
 * three built-in skin banks. Maps to the source `.osu`'s `[General]
 * SampleSet` (and is what {@link CirclesChart.hitSampleSet} carries).
 * `normal` is the bright default; `soft` is the gentle/quieter bank;
 * `drum` is the dry, punchy taiko-style bank.
 */
export type HitSampleSet = 'normal' | 'soft' | 'drum';

/**
 * Valid {@link HitSampleSet} values, exported so the importer/patcher
 * and validator share one source of truth.
 */
export const HIT_SAMPLE_SETS: ReadonlyArray<HitSampleSet> = ['normal', 'soft', 'drum'];

/**
 * osu! hitsound *addition* bit values. The source format packs these
 * into one bitmask per hit (`[HitObjects]` field 4, or a slider's head
 * edge-sound). Bit 0 (`normal`) is intentionally NOT modelled here: the
 * Circles base hit-click already covers "you hit it", so we only import
 * the accents a mapper layered on top — the whistle/finish/clap pattern
 * that reconstructs the song's percussion as you play. The importer
 * masks bit 0 out before persisting, so a plain note stores no
 * {@link CircleHitObject.hitSound} at all.
 */
export const HIT_SOUND_WHISTLE = 2;
export const HIT_SOUND_FINISH = 4;
export const HIT_SOUND_CLAP = 8;
/** Mask selecting only the imported addition bits (whistle|finish|clap). */
export const HIT_SOUND_ADDITION_MASK =
  HIT_SOUND_WHISTLE | HIT_SOUND_FINISH | HIT_SOUND_CLAP;

/**
 * One scoreable element on the Circles playfield. The renderer draws an
 * approach circle that shrinks to a hit ring at `beat`; the player must
 * click within ±the configured timing window AND inside the hit radius.
 *
 * Sliders extend that contract with a curved path the player must hold
 * along after the head click — see {@link CircleHitObject.slider}.
 * Spinners carry an `endBeat` and are auto-resolved by the engine —
 * they stay in the stream so timing density stays faithful to the
 * source map even though we do not implement spin-counting yet.
 */
export interface CircleHitObject {
  id: string;
  beat: number;
  /** osu playfield x coordinate (0..{@link OSU_PLAYFIELD_WIDTH}). */
  x: number;
  /** osu playfield y coordinate (0..{@link OSU_PLAYFIELD_HEIGHT}). */
  y: number;
  /**
   * Combo-colour group index (0..3 in source maps; we cycle the chart
   * palette through the same length). Renderer uses it to colour the
   * approach + hit rings.
   */
  comboIndex: number;
  /** True if this object starts a new combo group (osu's "new combo" flag). */
  newCombo: boolean;
  kind: CircleHitObjectKind;
  /**
   * Imported osu! hitsound *addition* bitmask — a bitwise-OR of
   * {@link HIT_SOUND_WHISTLE} / {@link HIT_SOUND_FINISH} /
   * {@link HIT_SOUND_CLAP}. Drives the percussive accent layered on
   * the base hit-click when this object is hit (see the runtime
   * `circlesHitsound` synth). For a slider this is the *head* sound
   * (osu's edge-sound[0], falling back to the row's hitSound field).
   *
   * Absent (not `0`) for plain notes — the importer omits the field
   * when no addition is set, so plain maps add zero JSON weight. The
   * normal-set base click already plays for every hit regardless. The
   * addition *timbre* (which skin bank) comes from the chart-level
   * {@link CirclesChart.hitSampleSet}.
   */
  hitSound?: number;
  /** Spinner end beat. Undefined for plain circles + sliders. */
  spinnerEndBeat?: number;
  /**
   * Slider geometry + timing. Required when {@link kind} is
   * `'slider'`, undefined otherwise. The slider head sits at
   * (`x`, `y`); `slider.controlPoints` are the post-head waypoints
   * from the source `.osu` row's `<curveType>|x:y|...` payload, in
   * osu playfield coordinates.
   */
  slider?: SliderHitData;
}

/**
 * Slider-specific extension fields. Lives as a sub-object so a
 * `kind === 'circle'` row never carries dead slider keys, and so
 * future fields (combo-tick scoring, slider sliders, etc.) attach
 * cleanly without bloating the top-level interface.
 *
 * Geometry is stored in osu units. `endBeat` and `tickBeats` are
 * pre-computed at import time so the engine and renderer don't have
 * to re-derive them from `length / sliderMultiplier / velocity` per
 * frame — slider timing math depends on the chart's full
 * `[TimingPoints]` walk and that's expensive to redo at runtime.
 */
export interface SliderHitData {
  curveType: SliderCurveType;
  /**
   * Post-head control points exactly as the source `.osu` row's
   * `<x>:<y>|...` payload — the head at (`obj.x`, `obj.y`) is NOT
   * included here. Engine and renderer prepend the head before
   * passing to `buildSliderPath` from `./osuCurves.js` (which
   * expects index 0 to BE the head). Storing them post-head keeps
   * the JSON identical to the importer's parse output and avoids
   * doubling the head bytes per slider in the wire format.
   *
   * Validation invariants:
   *   - `length >= 1` (at least one post-head anchor — head-only
   *     sliders would render as a static circle with no body).
   */
  controlPoints: Vec2[];
  /**
   * Geometric length of the slider path in osu units, exactly as
   * the source `.osu` row's 8th field. The actual sampled polyline
   * may be slightly longer or shorter depending on curve fidelity;
   * the engine clamps distance queries to this value so slider tail
   * hit-detection lands at the mapper's intended endpoint.
   */
  length: number;
  /**
   * Total path traversals. `1` = head→tail, `2` = head→tail→head
   * (one repeat), `3` = head→tail→head→tail (two repeats), etc.
   * Matches osu's `slides` field semantics.
   */
  slides: number;
  /**
   * Beat at which the slider's last edge resolves (after all repeats).
   * Engine auto-resolves the slider at this beat if the tail wasn't
   * caught by a release-at-tail event first.
   */
  endBeat: number;
  /**
   * Beats at which slider ticks fire (excludes the head and tail).
   * Pre-sorted ascending. Each tick the player is still holding
   * inside the follow-circle awards a slider-tick combo+score.
   * Empty array = no ticks (very short sliders).
   */
  tickBeats: number[];
  /**
   * Beats at which a repeat-edge fires (excludes head and tail).
   * Each repeat-edge that lands while the player is holding inside
   * the follow-circle awards a slider-tick combo+score AND
   * triggers the renderer's reverse-arrow flip.
   * Pre-sorted ascending. Empty array when slides === 1.
   */
  repeatBeats: number[];
}

/**
 * Tolerance applied when validating that a circle's beat lines up with
 * an audio onset. Circles is more permissive than Pulse's
 * {@link import('./pulseChart.js').PAD_ONSET_TOLERANCE_MS} because osu!
 * authors sometimes place circles a few ms off the strongest onset to
 * fit visual patterns; the source maps are still musical, just looser.
 */
export const CIRCLE_ONSET_TOLERANCE_MS = 60;

/**
 * Audible-circle limit per chart. osu! Insane sets routinely have
 * hundreds of objects in a 90-second cut; cap is generous so a single
 * dense map cannot wedge the engine.
 */
export const MAX_CIRCLE_HIT_OBJECTS = 2000;

/**
 * Circles-mode chart payload. All osu-style circles share the same
 * approach + radius parameters (varying these per-circle is not faithful
 * to the source format and would just complicate the renderer).
 */
export interface CirclesChart extends BaseChart {
  mode: 'circles';
  hitObjects: CircleHitObject[];
  /**
   * Approach window expressed in beats. Required for backwards
   * compatibility with the original BPM-coupled reading model and
   * legacy charts that pre-date {@link approachMs}.
   *
   * For new charts, prefer {@link approachMs} — it gives osu!-style
   * BPM-independent reading speed (a "Normal" map at 86 BPM and at
   * 174 BPM feel the same to read), and it's what every builtin
   * variant tuning sets. When both are present, `approachMs` wins;
   * `approachBeats` is then just a derived schema value computed as
   * `approachMs × bpm / 60_000`.
   */
  approachBeats: number;
  /**
   * Wall-clock approach time in milliseconds. Decouples reading time
   * from BPM the way osu!'s AR formula does — at AR 9 (~600 ms) a
   * player gets the same reaction window on a 120 BPM jazz track and
   * a 240 BPM DnB map. The engine and renderer compute approach
   * length from this value when present and ignore {@link
   * approachBeats}.
   *
   * Range mirrors osu!'s preempt range: AR 0 = 1800 ms, AR 10 = 450 ms.
   * Builtin variants ship at 1320 / 950 / 720 ms (Normal / Hard /
   * Extreme — ~AR4 / AR6.7 / AR8.2 respectively, deliberately ~20-30%
   * friendlier than canonical osu).
   */
  approachMs?: number;
  /**
   * Hit-circle radius in osu playfield units. osu's CS scale ranges
   * roughly 30–55 units (CS5 ≈ 38, CS3 ≈ 50). We default builtins to ~45.
   */
  circleRadius: number;
  /**
   * Per-chart perfect timing window in milliseconds. Optional — when
   * absent the engine and renderer fall back to the global
   * {@link import('./gameTypes.js').PERFECT_WINDOW_MS}. Hard / Extreme
   * variants set tighter values so harder maps demand more precise
   * timing in addition to faster approach speeds.
   */
  perfectWindowMs?: number;
  /**
   * Per-chart good timing window in milliseconds. See
   * {@link CirclesChart.perfectWindowMs}.
   */
  goodWindowMs?: number;
  /**
   * Per-chart "ok" (third tier) timing window in milliseconds. The
   * mercy band beyond `goodWindowMs` where a tap still registers as
   * a hit (with reduced score, no HP cost, combo intact) instead of
   * a punishing miss. Mirrors osu!'s 50 judgement.
   *
   * When absent the engine falls back to the global
   * {@link import('./gameTypes.js').OK_WINDOW_MS}.
   */
  okWindowMs?: number;
  /**
   * Per-chart stacking leniency in [0, 1]. Multiplied by the
   * approach window to give the maximum beat gap between two
   * coincident-position circles that still get visually stacked.
   * 0 disables stacking entirely; 1 stacks anything visible at the
   * same time. Default 0.7 (matches the global STACK_LENIENCY_BEATS_MUL
   * — kept for charts that don't override).
   */
  stackLeniency?: number;
  /**
   * Mapper-defined combo-colour palette imported from a source `.osu`
   * `[Colours]` section. The renderer cycles through this list per
   * combo group (`obj.comboIndex` is the group index, modulo length).
   * When absent — or empty — the renderer falls back to its built-in
   * 4-cycle. osu's spec allows up to 8 entries, so we cap there.
   *
   * Each entry must be a `#RRGGBB` hex string. Source files encode
   * colours as RGB triples (`Combo1 : 255,128,128`); the importer
   * converts them to hex before persisting.
   */
  comboColours?: string[];
  /**
   * Beat ranges where the source map flagged kiai mode active. Imported
   * from each `[TimingPoints]` line's effects bit 0; the importer
   * collapses adjacent regions into half-open `[startBeat, endBeat)`
   * intervals.
   *
   * The renderer brightens the playfield and pulses hit-circles in
   * sync with the beat during these ranges, and emits a one-time
   * star burst at each section's start. Sections may not overlap and
   * must be sorted ascending by `startBeat`.
   *
   * Optional: maps without kiai sections leave the field absent.
   */
  kiaiSections?: KiaiSection[];
  /**
   * Slider velocity multiplier from the source `.osu`'s
   * `[Difficulty] SliderMultiplier`. osu defaults to 1.4; the
   * actual range mappers use is roughly 1.0–3.0. The importer
   * already pre-computed slider end-beats and tick-beats from this
   * value, so the field is largely informational at runtime — but
   * we persist it so a future renderer or replay tool can re-derive
   * tick spacing without re-walking timing points.
   *
   * Optional: charts without sliders omit it. Required when at
   * least one hit object has `kind === 'slider'`.
   */
  sliderMultiplier?: number;
  /**
   * Slider tick rate from the source `.osu`'s
   * `[Difficulty] SliderTickRate` — ticks per beat (1 = one tick
   * per beat, 2 = two, etc.). osu defaults to 1.0.
   *
   * Same status as {@link sliderMultiplier}: pre-computed
   * `tickBeats` arrays are the source of truth at runtime; this is
   * persisted for re-derivation tooling.
   *
   * Optional: charts without sliders omit it.
   */
  sliderTickRate?: number;
  /**
   * Skin sample-set (timbre bank) for this chart's imported hitsound
   * additions, from the source `.osu`'s `[General] SampleSet`. Governs
   * how the whistle/finish/clap accents (see {@link
   * CircleHitObject.hitSound}) *sound* — `soft` accents are gentler,
   * `drum` drier. The accent *placement* is per-object; the bank is
   * chart-wide.
   *
   * Optional: omitted when `'normal'` (the default bank) or when the
   * chart carries no hitsound additions at all. Absent ⇒ `'normal'`.
   */
  hitSampleSet?: HitSampleSet;
}

/**
 * One contiguous run of kiai-active timing in the source map, as a
 * half-open beat interval `[startBeat, endBeat)`.
 *
 * "Half-open" matches osu's runtime semantics — the kiai flag is
 * "active at time T" iff the most recent timing point at-or-before T
 * has bit 0 set, so a kiai-off timing point at T2 ends the run at T2
 * (T2 itself is no longer kiai). Persisting `endBeat` as exclusive
 * keeps the renderer's "is this beat in any kiai section?" check a
 * simple `start <= b && b < end`.
 */
export interface KiaiSection {
  startBeat: number;
  endBeat: number;
}

/**
 * Effective approach time in beats. If the chart supplies a
 * BPM-independent {@link CirclesChart.approachMs}, derive the beat
 * count from BPM (matching osu!'s wall-clock approach semantics);
 * otherwise fall back to the BPM-coupled {@link
 * CirclesChart.approachBeats}.
 *
 * Used by both the renderer and (indirectly, via
 * {@link import('./engine/circles/core').buildCirclesEngineConfig})
 * the engine so the on-screen approach window stays exactly in sync
 * with the timing the engine actually accepts. Re-deriving in two
 * places was previously the source of subtle ms-of-drift bugs at
 * unusual BPMs.
 */
export function getEffectiveApproachBeats(chart: CirclesChart): number {
  if (chart.approachMs === undefined || chart.bpm <= 0) {
    return chart.approachBeats;
  }
  const beatsPerMs = chart.bpm / 60_000;
  return chart.approachMs * beatsPerMs;
}

export function validateCirclesChart(chart: CirclesChart): ChartValidationResult {
  const issues: ChartValidationIssue[] = [];

  validateBaseFields(chart, issues);

  if (!Array.isArray(chart.hitObjects) || chart.hitObjects.length === 0) {
    issues.push({ level: 'error', message: 'Circles chart needs at least one hit object' });
  } else if (chart.hitObjects.length > MAX_CIRCLE_HIT_OBJECTS) {
    issues.push({
      level: 'error',
      message: `hitObjects ${chart.hitObjects.length} exceeds limit ${MAX_CIRCLE_HIT_OBJECTS}`,
    });
  }

  // approachBeats is range-checked only when it's the source of truth
  // (chart.approachMs absent). When approachMs is present it's the
  // primary reading-time knob and approachBeats is just a derived
  // schema value (= approachMs × bpm / 60_000) — at high BPMs that
  // derivation can legitimately exceed the (0, 8] historical cap
  // even though the wall-clock reading time is sensible. We still
  // require a positive finite value either way so the legacy
  // BPM-coupled code paths can't divide by zero.
  if (!Number.isFinite(chart.approachBeats) || chart.approachBeats <= 0) {
    issues.push({
      level: 'error',
      message: `approachBeats ${chart.approachBeats} must be > 0`,
    });
  } else if (chart.approachMs === undefined && chart.approachBeats > 8) {
    issues.push({
      level: 'error',
      message: `approachBeats ${chart.approachBeats} must be ≤ 8 when approachMs is not set`,
    });
  }
  if (chart.approachMs !== undefined) {
    // Range mirrors osu!'s preempt curve: AR 10 = 450 ms (very fast),
    // AR 0 = 1800 ms (very slow). 200 ms lower bound stays a touch
    // below AR 11 territory so charts can push reading speed without
    // becoming unreadable; 2500 ms upper bound covers tutorial-grade
    // gentleness without pushing into "stale circle on screen" land.
    if (
      !Number.isFinite(chart.approachMs) ||
      chart.approachMs < 200 ||
      chart.approachMs > 2500
    ) {
      issues.push({
        level: 'error',
        message: `approachMs ${chart.approachMs} must be in [200, 2500] ms`,
      });
    }
  }
  if (chart.stackLeniency !== undefined) {
    if (!Number.isFinite(chart.stackLeniency) || chart.stackLeniency < 0 || chart.stackLeniency > 1) {
      issues.push({
        level: 'error',
        message: `stackLeniency ${chart.stackLeniency} must be in [0, 1]`,
      });
    }
  }
  if (!Number.isFinite(chart.circleRadius) || chart.circleRadius < 10 || chart.circleRadius > 100) {
    issues.push({
      level: 'error',
      message: `circleRadius ${chart.circleRadius} out of range (10-100 osu units)`,
    });
  }
  // Optional per-chart timing-window overrides. When supplied, every
  // present window must be finite + in range, and the tiers must be
  // ordered (perfect ≤ good ≤ ok). The strict ordering is what makes
  // judge() partition the timing axis cleanly.
  const TIMING_WINDOW_MIN_MS = 5;
  const TIMING_WINDOW_MAX_MS = 500;
  const tierFields = ['perfectWindowMs', 'goodWindowMs', 'okWindowMs'] as const;
  for (const field of tierFields) {
    const value = chart[field];
    if (value !== undefined) {
      if (
        !Number.isFinite(value) ||
        value < TIMING_WINDOW_MIN_MS ||
        value > TIMING_WINDOW_MAX_MS
      ) {
        issues.push({
          level: 'error',
          message: `${field} ${value} must be in [${TIMING_WINDOW_MIN_MS}, ${TIMING_WINDOW_MAX_MS}] ms`,
        });
      }
    }
  }
  if (
    chart.perfectWindowMs !== undefined &&
    chart.goodWindowMs !== undefined &&
    Number.isFinite(chart.perfectWindowMs) &&
    Number.isFinite(chart.goodWindowMs) &&
    chart.perfectWindowMs > chart.goodWindowMs
  ) {
    issues.push({
      level: 'error',
      message: `perfectWindowMs ${chart.perfectWindowMs} must be ≤ goodWindowMs ${chart.goodWindowMs}`,
    });
  }
  if (
    chart.goodWindowMs !== undefined &&
    chart.okWindowMs !== undefined &&
    Number.isFinite(chart.goodWindowMs) &&
    Number.isFinite(chart.okWindowMs) &&
    chart.goodWindowMs > chart.okWindowMs
  ) {
    issues.push({
      level: 'error',
      message: `goodWindowMs ${chart.goodWindowMs} must be ≤ okWindowMs ${chart.okWindowMs}`,
    });
  }
  // Direct perfect ≤ ok check catches the `goodWindowMs` undefined
  // case the two pairwise checks miss (chart with `perfectWindowMs:
  // 200, okWindowMs: 150` and no `goodWindowMs` would otherwise
  // produce unreachable lower tiers because the engine clamps
  // `okMs = max(goodMs, rawOkMs)` against the global good fallback).
  if (
    chart.perfectWindowMs !== undefined &&
    chart.okWindowMs !== undefined &&
    Number.isFinite(chart.perfectWindowMs) &&
    Number.isFinite(chart.okWindowMs) &&
    chart.perfectWindowMs > chart.okWindowMs
  ) {
    issues.push({
      level: 'error',
      message: `perfectWindowMs ${chart.perfectWindowMs} must be ≤ okWindowMs ${chart.okWindowMs}`,
    });
  }

  validateHitObjects(chart, issues);

  return {
    ok: !issues.some((i) => i.level === 'error'),
    issues,
  };
}

function validateBaseFields(chart: CirclesChart, issues: ChartValidationIssue[]): void {
  if (!chart.id) {
    issues.push({ level: 'error', message: 'Chart id is required' });
  }
  const title = chart.title ?? '';
  if (!title.trim()) {
    issues.push({ level: 'error', message: 'Chart title is required' });
  }
  if (title.length > CHART_LIMITS.maxTitleLength) {
    issues.push({
      level: 'error',
      message: `Title exceeds ${CHART_LIMITS.maxTitleLength} chars`,
    });
  }
  if ((chart.description ?? '').length > CHART_LIMITS.maxDescriptionLength) {
    issues.push({
      level: 'error',
      message: `Description exceeds ${CHART_LIMITS.maxDescriptionLength} chars`,
    });
  }
  if (chart.mapper !== undefined) {
    if (typeof chart.mapper !== 'string' || chart.mapper.trim().length === 0) {
      issues.push({
        level: 'error',
        message: 'mapper, when present, must be a non-empty string',
      });
    } else if (chart.mapper.length > CHART_LIMITS.maxMapperLength) {
      issues.push({
        level: 'error',
        message: `Mapper exceeds ${CHART_LIMITS.maxMapperLength} chars`,
      });
    }
  }
  // Upper bound 500 covers osu! beatmaps where mappers chart at half-time
  // or double-time grids: e.g. Different Heaven's "Nekozilla" appears in
  // some sets at 400 BPM (a 3:2 multiple of the perceived 128 BPM groove).
  // The chart's BPM is just metadata-and-grid; physical audio playback
  // speed is unaffected.
  if (!Number.isFinite(chart.bpm) || chart.bpm < 40 || chart.bpm > 500) {
    issues.push({
      level: 'error',
      message: `BPM ${chart.bpm} out of range (40-500)`,
    });
  }
  if (!Number.isFinite(chart.totalBeats) || chart.totalBeats <= 0) {
    issues.push({ level: 'error', message: 'totalBeats must be > 0' });
  }
  if (Number.isFinite(chart.totalBeats) && chart.totalBeats > CHART_LIMITS.maxTotalBeats) {
    issues.push({
      level: 'error',
      message: `totalBeats ${chart.totalBeats} exceeds limit ${CHART_LIMITS.maxTotalBeats}`,
    });
  }
  if (
    !Number.isInteger(chart.difficulty) ||
    chart.difficulty < 1 ||
    chart.difficulty > 5
  ) {
    issues.push({
      level: 'error',
      message: `difficulty ${chart.difficulty} must be integer 1-5`,
    });
  }
  if (!(CIRCLES_VARIANTS as ReadonlyArray<string>).includes(chart.variant)) {
    issues.push({
      level: 'error',
      message: `variant ${chart.variant} must be one of ${CIRCLES_VARIANTS.join(', ')}`,
    });
  }

  if (!chart.audio || typeof chart.audio.src !== 'string' || chart.audio.src.length === 0) {
    issues.push({ level: 'error', message: 'audio.src is required' });
  } else {
    if (!Number.isFinite(chart.audio.bpm) || chart.audio.bpm !== chart.bpm) {
      issues.push({
        level: 'error',
        message: `audio.bpm (${chart.audio.bpm}) must match chart.bpm (${chart.bpm})`,
      });
    }
    if (!Number.isFinite(chart.audio.offsetMs) || chart.audio.offsetMs < 0) {
      issues.push({ level: 'error', message: 'audio.offsetMs must be a non-negative number' });
    }
    if (!Number.isFinite(chart.audio.durationSec) || chart.audio.durationSec <= 0) {
      issues.push({ level: 'error', message: 'audio.durationSec must be > 0' });
    }
    if ((chart.audio.onsets?.length ?? 0) > CHART_LIMITS.maxOnsets) {
      issues.push({
        level: 'error',
        message: `audio.onsets ${chart.audio.onsets.length} exceeds limit ${CHART_LIMITS.maxOnsets}`,
      });
    }
  }

  for (const key of REQUIRED_PALETTE_KEYS) {
    const value = chart.palette?.[key];
    if (typeof value !== 'string' || !HEX_COLOUR_RE.test(value)) {
      issues.push({
        level: 'error',
        message: `palette.${key} must be a hex colour like #RRGGBB`,
      });
    }
  }

  if (chart.kiaiSections !== undefined) {
    if (!Array.isArray(chart.kiaiSections)) {
      issues.push({ level: 'error', message: 'kiaiSections must be an array when present' });
    } else if (chart.kiaiSections.length === 0) {
      // Same "absent vs empty" contract as comboColours — writers drop
      // empty arrays before they reach the chart, so an explicit `[]`
      // here is a hand-edit / test confusion. Reject.
      issues.push({
        level: 'error',
        message: 'kiaiSections must be omitted entirely (or undefined) rather than empty — the renderer skips kiai effects when the field is absent',
      });
    } else {
      let prevStart = -Infinity;
      let prevEnd = -Infinity;
      chart.kiaiSections.forEach((section, idx) => {
        if (typeof section?.startBeat !== 'number' || !Number.isFinite(section.startBeat)) {
          issues.push({
            level: 'error',
            message: `kiaiSections[${idx}].startBeat must be a finite number`,
          });
          return;
        }
        if (typeof section.endBeat !== 'number' || !Number.isFinite(section.endBeat)) {
          issues.push({
            level: 'error',
            message: `kiaiSections[${idx}].endBeat must be a finite number`,
          });
          return;
        }
        if (section.endBeat <= section.startBeat) {
          issues.push({
            level: 'error',
            message: `kiaiSections[${idx}] endBeat (${section.endBeat}) must be > startBeat (${section.startBeat})`,
          });
        }
        // Reject sections that fall outside [0, totalBeats]: the
        // deriver clamps already, but a hand-edited or future-writer
        // chart could ship out-of-range data that the renderer's
        // `activeKiaiIndex` would silently never match. Surface it
        // up front instead of letting the bytes become dead data.
        if (section.startBeat < 0) {
          issues.push({
            level: 'error',
            message: `kiaiSections[${idx}] startBeat (${section.startBeat}) is before chart start (must be ≥ 0)`,
          });
        }
        if (section.endBeat > chart.totalBeats) {
          issues.push({
            level: 'error',
            message: `kiaiSections[${idx}] endBeat (${section.endBeat}) is past chart totalBeats (${chart.totalBeats})`,
          });
        }
        // Distinguish "unsorted" from "overlapping" up front: an
        // operator hand-editing a chart with `[{10, 20}, {30, 40},
        // {5, 8}]` would see a phantom "overlap with section ending
        // at 40" if we only carried `prevEnd` forward. The deriver
        // always emits sorted output; this branch only ever fires
        // for bad hand-edits, but its error message points at the
        // real problem.
        if (section.startBeat < prevStart) {
          issues.push({
            level: 'error',
            message: `kiaiSections[${idx}] startBeat (${section.startBeat}) is before kiaiSections[${idx - 1}].startBeat (${prevStart}) — sections must be sorted ascending by startBeat`,
          });
        } else if (section.startBeat < prevEnd) {
          issues.push({
            level: 'error',
            message: `kiaiSections[${idx}] startBeat (${section.startBeat}) overlaps the previous section ending at ${prevEnd}`,
          });
        }
        prevStart = section.startBeat;
        prevEnd = section.endBeat;
      });
    }
  }

  if (chart.sliderMultiplier !== undefined) {
    if (
      !Number.isFinite(chart.sliderMultiplier) ||
      chart.sliderMultiplier <= 0 ||
      chart.sliderMultiplier > 10
    ) {
      // osu's UI capped at SliderMultiplier 3.6 historically; we
      // accept a generous 10 because rebuilt maps still ship with
      // legitimate values up to the high single digits.
      issues.push({
        level: 'error',
        message: `sliderMultiplier ${chart.sliderMultiplier} must be in (0, 10]`,
      });
    }
  }
  if (chart.sliderTickRate !== undefined) {
    if (
      !Number.isFinite(chart.sliderTickRate) ||
      chart.sliderTickRate <= 0 ||
      chart.sliderTickRate > 8
    ) {
      // osu's allowed range is 0.5–8; permissive lower bound covers
      // legacy maps that ship with 0.25/0.5 tick rates.
      issues.push({
        level: 'error',
        message: `sliderTickRate ${chart.sliderTickRate} must be in (0, 8]`,
      });
    }
  }

  if (chart.comboColours !== undefined) {
    if (!Array.isArray(chart.comboColours)) {
      issues.push({ level: 'error', message: 'comboColours must be an array of hex strings when present' });
    } else if (chart.comboColours.length === 0) {
      // The writers (importer + buildCirclesChart) drop empty arrays
      // before they reach the chart, so an empty `comboColours` here
      // means a hand-edited or hand-built chart that's confused about
      // the contract. Surface it loudly rather than silently falling
      // back to the renderer's default cycle.
      issues.push({
        level: 'error',
        message: 'comboColours must be omitted entirely (or undefined) rather than empty — the renderer falls back to its built-in cycle when the field is absent',
      });
    } else if (chart.comboColours.length > 8) {
      // osu! caps `[Colours]` at Combo1..Combo8, so anything past 8
      // would be data the source can't legally express. Reject loud
      // rather than silently truncate — the source must be malformed
      // or hand-edited.
      issues.push({
        level: 'error',
        message: `comboColours has ${chart.comboColours.length} entries; max 8`,
      });
    } else {
      chart.comboColours.forEach((colour, idx) => {
        // Stricter than the shared `HEX_COLOUR_RE` (which accepts
        // `#RGB` and `#RRGGBBAA`): the renderer's `hexToRgba` reads
        // the first 6 hex chars after `#` and would silently produce
        // NaN for 3-digit shorthand or drop the alpha byte for 8-digit.
        // Keep this contract explicit so a hand-edited JSON can't
        // sneak invalid data past validation.
        if (typeof colour !== 'string' || !/^#[0-9a-fA-F]{6}$/.test(colour)) {
          issues.push({
            level: 'error',
            message: `comboColours[${idx}] must be a hex colour like #RRGGBB`,
          });
        }
      });
    }
  }

  if (
    chart.hitSampleSet !== undefined &&
    !(HIT_SAMPLE_SETS as ReadonlyArray<string>).includes(chart.hitSampleSet)
  ) {
    issues.push({
      level: 'error',
      message: `hitSampleSet ${chart.hitSampleSet} must be one of ${HIT_SAMPLE_SETS.join(', ')}`,
    });
  }
}

function validateHitObjects(chart: CirclesChart, issues: ChartValidationIssue[]): void {
  if (!Array.isArray(chart.hitObjects)) {
    return;
  }
  const ids = new Set<string>();
  const sorted = [...chart.hitObjects].sort((a, b) => a.beat - b.beat);
  const beatsPerMs =
    Number.isFinite(chart.bpm) && chart.bpm > 0 ? chart.bpm / 60_000 : 0;
  const onsetToleranceBeats = CIRCLE_ONSET_TOLERANCE_MS * beatsPerMs;
  const hasOnsets = (chart.audio?.onsets?.length ?? 0) > 0;

  for (const obj of sorted) {
    if (!obj.id) {
      issues.push({ level: 'error', message: 'Hit object missing id', beat: obj.beat });
      continue;
    }
    if (ids.has(obj.id)) {
      issues.push({ level: 'error', message: `Duplicate hit object id ${obj.id}` });
    }
    ids.add(obj.id);

    if (!Number.isFinite(obj.beat) || obj.beat < 0 || obj.beat > chart.totalBeats) {
      issues.push({
        level: 'error',
        message: `Hit object ${obj.id} at beat ${obj.beat} outside [0, ${chart.totalBeats}]`,
        beat: obj.beat,
      });
    }
    if (
      !Number.isFinite(obj.x) ||
      obj.x < 0 ||
      obj.x > OSU_PLAYFIELD_WIDTH ||
      !Number.isFinite(obj.y) ||
      obj.y < 0 ||
      obj.y > OSU_PLAYFIELD_HEIGHT
    ) {
      issues.push({
        level: 'error',
        message: `Hit object ${obj.id} position (${obj.x}, ${obj.y}) outside playfield (0..${OSU_PLAYFIELD_WIDTH} x 0..${OSU_PLAYFIELD_HEIGHT})`,
        beat: obj.beat,
      });
    }
    if (obj.hitSound !== undefined) {
      if (
        !Number.isInteger(obj.hitSound) ||
        obj.hitSound <= 0 ||
        (obj.hitSound & ~HIT_SOUND_ADDITION_MASK) !== 0
      ) {
        issues.push({
          level: 'error',
          message: `Hit object ${obj.id} hitSound ${obj.hitSound} must be a positive bitmask of whistle(${HIT_SOUND_WHISTLE})|finish(${HIT_SOUND_FINISH})|clap(${HIT_SOUND_CLAP})`,
          beat: obj.beat,
        });
      }
    }

    if (obj.kind === 'spinner') {
      const endBeat = obj.spinnerEndBeat ?? 0;
      if (!Number.isFinite(obj.spinnerEndBeat) || endBeat <= obj.beat) {
        issues.push({
          level: 'error',
          message: `Spinner ${obj.id} needs spinnerEndBeat strictly greater than start beat ${obj.beat}`,
          beat: obj.beat,
        });
      } else if (endBeat > chart.totalBeats) {
        // Spinner that finishes past the end of the chart can never be
        // resolved by the engine (the tick loop never advances past
        // totalBeats), so it would silently disappear without scoring.
        issues.push({
          level: 'error',
          message: `Spinner ${obj.id} ends at beat ${endBeat} past chart totalBeats ${chart.totalBeats}`,
          beat: obj.beat,
        });
      }
    } else if (obj.kind === 'slider') {
      validateSlider(obj, chart, issues);
    }

    if (hasOnsets && obj.kind === 'circle' && Number.isFinite(obj.beat)) {
      const nearest = findNearestOnset(chart.audio, obj.beat);
      if (nearest && Math.abs(nearest.beat - obj.beat) > onsetToleranceBeats) {
        issues.push({
          level: 'warning',
          message: `Hit object ${obj.id} at beat ${obj.beat.toFixed(2)} is more than ${CIRCLE_ONSET_TOLERANCE_MS}ms from the nearest audio onset (beat ${nearest.beat.toFixed(2)}).`,
          beat: obj.beat,
        });
      }
    }
  }
}

const ALLOWED_CURVE_TYPES = new Set<SliderCurveType>(['L', 'B', 'P', 'C']);

function validateSlider(
  obj: CircleHitObject,
  chart: CirclesChart,
  issues: ChartValidationIssue[],
): void {
  const data = obj.slider;
  if (!data) {
    issues.push({
      level: 'error',
      message: `Slider ${obj.id} missing slider data block`,
      beat: obj.beat,
    });
    return;
  }
  if (!ALLOWED_CURVE_TYPES.has(data.curveType)) {
    issues.push({
      level: 'error',
      message: `Slider ${obj.id} has unknown curveType "${data.curveType}" (must be L/B/P/C)`,
      beat: obj.beat,
    });
  }
  if (!Array.isArray(data.controlPoints) || data.controlPoints.length === 0) {
    // Reject head-only sliders. A path built from `[head]` (engine
    // and renderer prepend the head before tessellating) would
    // collapse to a single-sample path with `length=0`, leaving
    // the slider ball pinned at the head for the entire window
    // and the tracking check trivially satisfied — awarding every
    // authored tick + repeat + tail as free tracking credit.
    issues.push({
      level: 'error',
      message: `Slider ${obj.id} needs at least one post-head control point`,
      beat: obj.beat,
    });
  } else {
    data.controlPoints.forEach((cp, idx) => {
      if (
        !cp ||
        !Number.isFinite(cp.x) ||
        !Number.isFinite(cp.y) ||
        cp.x < -OSU_PLAYFIELD_WIDTH ||
        cp.x > 2 * OSU_PLAYFIELD_WIDTH ||
        cp.y < -OSU_PLAYFIELD_HEIGHT ||
        cp.y > 2 * OSU_PLAYFIELD_HEIGHT
      ) {
        // Wider than the strict playfield bounds because osu mappers
        // legitimately route slider control points off-screen for
        // dramatic curves (the path itself stays largely on-field).
        // 2× width on each side is roomy enough for any real slider
        // and tight enough to catch corrupt input.
        issues.push({
          level: 'error',
          message: `Slider ${obj.id} control point ${idx} (${cp?.x}, ${cp?.y}) is far off-playfield`,
          beat: obj.beat,
        });
      }
    });
  }
  if (!Number.isFinite(data.length) || data.length <= 0) {
    issues.push({
      level: 'error',
      message: `Slider ${obj.id} length ${data.length} must be > 0`,
      beat: obj.beat,
    });
  }
  if (!Number.isInteger(data.slides) || data.slides < 1) {
    issues.push({
      level: 'error',
      message: `Slider ${obj.id} slides ${data.slides} must be a positive integer`,
      beat: obj.beat,
    });
  }
  if (!Number.isFinite(data.endBeat) || data.endBeat <= obj.beat) {
    issues.push({
      level: 'error',
      message: `Slider ${obj.id} endBeat ${data.endBeat} must be > start beat ${obj.beat}`,
      beat: obj.beat,
    });
  } else if (data.endBeat > chart.totalBeats) {
    issues.push({
      level: 'error',
      message: `Slider ${obj.id} endBeat ${data.endBeat} past chart totalBeats ${chart.totalBeats}`,
      beat: obj.beat,
    });
  }
  if (!Array.isArray(data.tickBeats)) {
    issues.push({
      level: 'error',
      message: `Slider ${obj.id} tickBeats must be an array`,
      beat: obj.beat,
    });
  } else {
    let prev = obj.beat;
    data.tickBeats.forEach((tb, idx) => {
      if (!Number.isFinite(tb) || tb <= obj.beat || tb >= data.endBeat) {
        issues.push({
          level: 'error',
          message: `Slider ${obj.id} tickBeats[${idx}] ${tb} must be in (${obj.beat}, ${data.endBeat})`,
          beat: obj.beat,
        });
      } else if (tb < prev) {
        issues.push({
          level: 'error',
          message: `Slider ${obj.id} tickBeats[${idx}] ${tb} is out of order (must be ascending)`,
          beat: obj.beat,
        });
      }
      prev = tb;
    });
  }
  if (!Array.isArray(data.repeatBeats)) {
    issues.push({
      level: 'error',
      message: `Slider ${obj.id} repeatBeats must be an array`,
      beat: obj.beat,
    });
  } else {
    // Slides=1 has zero repeat-edges; slides=N has N-1 repeat-edges.
    const expectedRepeats = Math.max(0, (data.slides ?? 1) - 1);
    if (data.repeatBeats.length !== expectedRepeats) {
      issues.push({
        level: 'error',
        message: `Slider ${obj.id} repeatBeats length ${data.repeatBeats.length} doesn't match slides=${data.slides} (expected ${expectedRepeats})`,
        beat: obj.beat,
      });
    }
    let prev = obj.beat;
    data.repeatBeats.forEach((rb, idx) => {
      if (!Number.isFinite(rb) || rb <= obj.beat || rb >= data.endBeat) {
        issues.push({
          level: 'error',
          message: `Slider ${obj.id} repeatBeats[${idx}] ${rb} must be in (${obj.beat}, ${data.endBeat})`,
          beat: obj.beat,
        });
      } else if (rb < prev) {
        issues.push({
          level: 'error',
          message: `Slider ${obj.id} repeatBeats[${idx}] ${rb} is out of order (must be ascending)`,
          beat: obj.beat,
        });
      }
      prev = rb;
    });
  }
}
